import {
  Component,
  EventEmitter,
  Input,
  Output,
  forwardRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { IDispositivo } from 'modelos/src';

/**
 * Selector de dispositivo reutilizable basado en ng-select.
 *
 * - Muestra `deviceName` y `deveui` (+ FW/HW si están), y busca por deviceName/deveui.
 * - Implementa ControlValueAccessor, por lo que se usa con `formControlName`
 *   o `[(ngModel)]` como cualquier control de formulario.
 * - El listado de dispositivos lo provee el padre vía `[dispositivos]`
 *   (así cada form aplica su propio filtrado/orden).
 * - Emite `(seleccion)` con el dispositivo elegido (o `undefined` al limpiar),
 *   para que el padre pueda derivar datos (UN / CO / Localidad, etc).
 *
 * Por defecto el valor del control es el `deveui`; se puede cambiar con
 * `[bindValue]` (ej: `_id`).
 */
@Component({
  selector: 'app-selector-dispositivo',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule],
  templateUrl: './selector-dispositivo.component.html',
  styleUrls: ['./selector-dispositivo.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectorDispositivoComponent),
      multi: true,
    },
  ],
})
export class SelectorDispositivoComponent implements ControlValueAccessor {
  private _dispositivos: IDispositivo[] = [];
  // El padre suele pasar un getter (ej. `dispositivosDisponibles`) que devuelve
  // un array NUEVO en cada ciclo de detección. ng-select reconstruye su lista
  // interna cuando cambia la referencia de `items`, lo que hace que la selección
  // recién hecha se comporte de forma errática. Guardamos una referencia estable
  // y solo la reemplazamos si el contenido cambió realmente.
  @Input() set dispositivos(v: IDispositivo[] | null | undefined) {
    const next = v ?? [];
    if (!this.mismaLista(this._dispositivos, next)) {
      this._dispositivos = next;
    }
  }
  get dispositivos(): IDispositivo[] {
    return this._dispositivos;
  }

  @Input() placeholder = 'Dispositivo';
  @Input() bindValue = 'deveui';
  @Input() appendTo = 'body';
  @Input() clearable = true;

  /** Dispositivo seleccionado (o undefined al limpiar). */
  @Output() seleccion = new EventEmitter<IDispositivo | undefined>();

  public value: unknown;
  public disabled = false;

  private onChange: (value: unknown) => void = () => {};
  public onTouched: () => void = () => {};

  // Compara dos listas por contenido (por _id/deveui) para no romper la
  // referencia de `items` cuando el padre reevalúa un getter.
  private mismaLista(a: IDispositivo[], b: IDispositivo[]): boolean {
    if (a === b) return true;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i]?._id !== b[i]?._id || a[i]?.deveui !== b[i]?.deveui) {
        return false;
      }
    }
    return true;
  }

  // Nombre / deveui como string (algunos items sentinela — ej. "Sin Asignar" en
  // filtros — traen deveui como objeto de query tipo { $eq: null }).
  public nombreOf(item: IDispositivo): string {
    return typeof item.deviceName === 'string' ? item.deviceName : '';
  }

  public euiOf(item: IDispositivo): string {
    return typeof item.deveui === 'string' ? item.deveui : '';
  }

  // Firmware: top-level `firmware`, o `config.firmwareNuc` en algunos tipos.
  public firmwareOf(item: IDispositivo): string | undefined {
    const fw = item.firmware || item.config?.['firmwareNuc'];
    return typeof fw === 'string' ? fw : undefined;
  }

  // Versión de hardware (NUC: "v1.0" | "v1.1" | "v2.0").
  public hardwareOf(item: IDispositivo): string | undefined {
    return typeof item.versionHardware === 'string'
      ? item.versionHardware
      : undefined;
  }

  // Búsqueda por deviceName y deveui (tolera items sin string en esos campos)
  public searchDispositivo = (term: string, item: IDispositivo): boolean => {
    const t = term.toLowerCase();
    return (
      this.nombreOf(item).toLowerCase().includes(t) ||
      this.euiOf(item).toLowerCase().includes(t)
    );
  };

  public onValueChange(value: unknown): void {
    this.value = value;
    this.onChange(value);
    const dispositivo = this.dispositivos.find(
      (d) =>
        (d as unknown as Record<string, unknown>)[this.bindValue] === value,
    );
    this.seleccion.emit(dispositivo);
  }

  // ControlValueAccessor
  public writeValue(value: unknown): void {
    this.value = value;
  }

  public registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
